/**
 * What pasted, dropped and typed text puts in (table A in the part 10 log): plain text as text, one paragraph per line
 * (A4); a source (this page's own copy, A5, or rich paste turned into source, A6) with each unit as the kind its own
 * parse gives it, so its markup stays markup where it still reads (table V decides).
 */
import { normalizeSource, parseChapter } from '@cockatrice/core';
import type { Piece } from './rewrite.ts';
import { unitsOf } from './units.ts';

/**
 * A4: plain text as text; several lines become paragraphs, blank lines dropped; a final line end ends the paragraph
 * (a whole line pasted at a line's start stays a line of its own, *deep-fix r1*).
 */
export function plainPieces(text: string): Piece[] {
  const normal = normalizeSource(text);
  const lines = normal.split('\n');
  const kept = lines.length === 1 ? lines : lines.filter((line) => line.trim() !== '');
  const pieces: Piece[] = [];
  for (const [i, line] of kept.entries()) {
    if (i > 0) pieces.push({ kind: 'block', text: '\n\n' });
    if (line !== '') pieces.push({ kind: 'text', text: line });
  }
  if (normal.endsWith('\n') && kept.length > 0) pieces.push({ kind: 'block', text: '\n\n' });
  return pieces;
}

/**
 * A5: a source's units as pieces of their kinds; a value (a link's destination or title, a note reference's label) as
 * the inline markup it is, a definition's label as block markup. With `copy`, this page's own copy: when the fragment
 * read alone doesn't read its text as the chapter did (a `- ` that was text now a list mark, a cut emphasis's `*` now
 * text), it goes in as what the copy showed, text only (*deep-fix r9*).
 */
export function sourcePieces(source: string, copy?: { readonly shown: string; readonly text: string }): Piece[] {
  const text = normalizeSource(source).replace(/\n+$/u, '');
  const units = unitsOf(parseChapter(text));
  if (copy !== undefined && units.filter((u) => u.kind === 'text').map((u) => u.show).join('') !== copy.text) return plainPieces(copy.shown);
  const pieces: Piece[] = [];
  for (const unit of units) {
    const kind = unit.kind === 'value' ? 'inline' : unit.kind === 'label' ? 'block' : unit.kind;
    const piece = kind === 'text' ? unit.show : text.slice(unit.from, unit.to);
    const last = pieces.at(-1);
    if (last?.kind === kind) pieces[pieces.length - 1] = { kind, text: last.text + piece };
    else pieces.push({ kind, text: piece });
  }
  return pieces;
}

